import { useMemo } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import { useMaterialUIController } from "context";
import { formatLocalDate, formatLocalTime, formatLocalDateTime } from "utils/dateFormatter";
import GenericListScreen from "components/GenericListScreen/GenericListScreen";
import { Column } from "components/GenericListScreen/types";

// Define the shape of the API Response item
interface DeviceInfoItem {
    seqId: number;
    userName: string;
    userEmail: string;
    deviceName: string;
    brand: string;
    manufacturer: string;
    model: string;
    os: string;
    systemName: string;
    isTablet: boolean;
    isActivated: boolean;
    crtDt: string | null; // Allow null for type safety
}

const DeviceInfoListScreen = () => {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    // Define table columns
    const columns = useMemo<Column<DeviceInfoItem>[]>(
        () => [
            {
                Header: "사용자",
                accessor: "userName",
                align: "left",
                width: "15%",
                Cell: ({ row }: any) => (
                    <MDBox>
                        <MDTypography variant="button" fontWeight="medium">
                            {row.original.userName}
                        </MDTypography>
                        <br />
                        <MDTypography variant="caption" color="text">
                            {row.original.userEmail}
                        </MDTypography>
                    </MDBox>
                )
            },
            { Header: "디바이스명", accessor: "deviceName", align: "left", width: "12%" },
            { Header: "브랜드", accessor: "brand", align: "left", width: "10%" },
            { Header: "모델", accessor: "model", align: "left", width: "12%" },
            {
                Header: "OS",
                accessor: "os",
                align: "center",
                width: "10%",
                Cell: ({ value }: any) => {
                    const osLower = value?.toLowerCase() || "";
                    let iconName = "help_outline";
                    let iconColor = "text";

                    if (osLower.includes("ios") || osLower.includes("iphone")) {
                        iconName = "apple";
                        iconColor = "white";
                    } else if (osLower.includes("android")) {
                        iconName = "android";
                        iconColor = "success";
                    } else if (osLower.includes("web")) {
                        iconName = "language";
                        iconColor = "info";
                    }

                    return (
                        <MDBox display="flex" alignItems="center" justifyContent="center">
                            <Icon color={iconColor as any} fontSize="large">{iconName}</Icon>
                        </MDBox>
                    );
                }
            },
            {
                Header: "타입",
                accessor: "isTablet",
                align: "center",
                width: "8%",
                Cell: ({ value }: any) => (
                    <MDTypography variant="caption">
                        {value ? "태블릿" : "모바일"}
                    </MDTypography>
                )
            },
            {
                Header: "활성화",
                accessor: "isActivated",
                align: "center",
                width: "8%",
                Cell: ({ value }: any) => (
                    <Chip
                        label={value ? "활성" : "비활성"}
                        color={value ? "success" : "default"}
                        size="small"
                        sx={{
                            fontWeight: "medium",
                            minWidth: 60,
                            ...(!value && {
                                backgroundColor: darkMode ? "rgba(255, 255, 255, 0.12)" : undefined,
                                color: darkMode ? "#ffffff" : undefined,
                                "& .MuiChip-label": {
                                    color: darkMode ? "#ffffff" : undefined,
                                },
                            }),
                        }}
                    />
                )
            },
            {
                Header: "등록일",
                accessor: "crtDt",
                align: "center",
                width: "15%",
                Cell: ({ value }: any) => (
                    <MDBox lineHeight={1}>
                        <MDTypography display="block" variant="caption" fontWeight="medium">
                            {formatLocalDate(value)}
                        </MDTypography>
                        <MDTypography variant="caption" color="text" fontWeight="regular">
                            {formatLocalTime(value)}
                        </MDTypography>
                    </MDBox>
                )
            },
        ],
        [darkMode]
    );

    // Transform API data to row format
    const dataTransform = (data: any): { items: DeviceInfoItem[], totalCount: number } => {
        const items = (data.items || []).map((device: any) => ({
            seqId: device.seqId,
            userName: device.userName || "-",
            userEmail: device.userEmail || "-",
            deviceName: device.deviceName || "-",
            brand: device.brand || "-",
            manufacturer: device.manufacturer || "-",
            model: device.model || "-",
            os: device.os || "-",
            systemName: device.systemName || "-",
            isTablet: device.isTablet,
            isActivated: device.isActivated,
            crtDt: device.crtDt || null,
        }));

        return {
            items,
            totalCount: data.totalCount || 0
        };
    };

    return (
        <GenericListScreen<DeviceInfoItem>
            title="디바이스 정보 ( DEVICE INFO )"
            icon="devices"
            apiName="Device.pagedList"
            columns={columns}
            dataTransform={dataTransform}
        />
    );
};

export default DeviceInfoListScreen;
